import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/hooks";
import dayjs from "dayjs";
import { MdAdd, MdClose, MdPersonAdd, MdRefresh } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import Button, { IconButton } from "components/button";
import Card from "components/card";
import EventCalendar from "components/calendar/EventCalendar";
import { useConfirm } from "components/dialog/ConfirmProvider";
import Loader from "components/loader/Loader";
import { PageContainer, PageHeader } from "components/page";
import { ErrorState } from "components/states";
import notify from "lib/notify";
import ServiceForm from "./service-form";
import AssignForm from "./assign-form";
import ServiceDetails from "./service-details";
import QuickFilters from "./components/QuickFilters";
import CalendarHeader from "./components/CalendarHeader";
import EventHoverPreview from "./components/EventHoverPreview";

const Calendar = () => {
  const params = useParams();
  const isNewService = useMatch("/admin/service-scheduling/services/new") !== null;
  const serviceId = isNewService ? "new" : params.id;
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!serviceId,
    onClose: () => {
      navigate("/admin/service-scheduling");
    },
  });

  const ref = useRef();
  const calendarRef = useRef(null);
  const confirm = useConfirm();

  const location = useLocation();
  const [service, setService] = useState({
    name: "",
    description: "",
    type: "",
    address: "",
    location: {
      type: "Point",
      coordinates: [0, 0],
    },
    date: "",
    startAt: "",
    endAt: "",
    schedule: "",
  });

  const [readonlyForm, setReadonlyForm] = useState(true);
  const [showAssign, setShowAssign] = useState(false);

  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [currentView, setCurrentView] = useState("timeGridWeek");

  const [activeDate, setActiveDate] = useState(new Date());
  const [churchServices, setChurchServices] = useState([]);
  const [worshipServices, setWorshipServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // CalendarHeader's SearchInput debounces, so this is already the settled term.
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    type: "all",
    schedule: "all",
    person: "all",
  });

  // Hover preview state
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);

  // Apply filters to church services
  const filteredServices = useMemo(() => {
    if (!churchServices) {
      return [];
    }

    return churchServices.filter((service) => {
      // Type filter
      if (filters.type !== "all" && service?.type?.toLowerCase() !== filters.type) {
        return false;
      }

      // Schedule filter
      if (filters.schedule !== "all" && service?.schedule !== filters.schedule) {
        return false;
      }

      // Person filter
      if (filters.person !== "all") {
        const hasPersonAssigned = service?.users?.some(
          (user) => user.id === parseInt(filters.person)
        );
        if (!hasPersonAssigned) return false;
      }

      return true;
    });
  }, [churchServices, filters]);

  const events = useMemo(() => {
    const allEvents = [];

    // Add regular church services
    if (filteredServices && filteredServices.length > 0) {
      const regularEvents = filteredServices.map((service) => {
        const dateStr = service?.date;
        let parsedDate;

        if (dateStr) {
          // Parse date in timezone-agnostic way to avoid date shifts
          if (dateStr.includes('T')) {
            const datePart = dateStr.split('T')[0];
            const [year, month, day] = datePart.split('-').map(Number);
            parsedDate = new Date(year, month - 1, day);
          } else if (dateStr.includes('/')) {
            const [month, day, year] = dateStr.split('/').map(Number);
            parsedDate = new Date(year, month - 1, day);
          } else if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-').map(Number);
            parsedDate = new Date(year, month - 1, day);
          } else {
            parsedDate = new Date(dateStr);
          }
        } else {
          parsedDate = new Date();
        }

        const [startHour, startMinute] = service?.startAt.split(":").map(Number);
        const [endHour, endMinute] = service?.endAt.split(":").map(Number);

        const start = new Date(parsedDate);
        start.setHours(startHour, startMinute || 0, 0, 0);

        const end = new Date(parsedDate);
        end.setHours(endHour, endMinute || 0, 0, 0);

        const serviceType = service?.type?.toLowerCase() || 'other';
        const serviceTypeClass = `service-type-${serviceType.replace(/\s+/g, '-')}`;

        return {
          id: service?.id,
          title: service?.name,
          borderColor: "transparent",
          start,
          end,
          className: `info ${serviceTypeClass}`,
          extendedProps: {
            origin: service,
            serviceType: serviceType,
          },
        };
      });
      allEvents.push(...regularEvents);
    }

    // Add recurring worship services
    if (worshipServices && worshipServices.length > 0) {
      worshipServices.forEach((worship) => {
        // day_of_week is stored as a number: 0 = Sunday, 1 = Monday, etc.
        const dayOfWeek = worship.day_of_week;
        if (dayOfWeek === undefined || dayOfWeek === null || dayOfWeek < 0 || dayOfWeek > 6) return;

        // Generate events for the visible calendar range (current month +/- 1 month)
        const start = new Date(activeDate);
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);

        const end = new Date(activeDate);
        end.setMonth(end.getMonth() + 2);
        end.setDate(0);

        // Find all dates matching this day of week in range
        const current = new Date(start);
        while (current <= end) {
          if (current.getDay() === dayOfWeek) {
            const [hours, minutes] = worship.time.split(':').map(Number);
            const eventStart = new Date(current);
            eventStart.setHours(hours, minutes || 0, 0, 0);

            const duration = parseInt(worship.duration) || 60;
            const eventEnd = new Date(eventStart);
            eventEnd.setMinutes(eventEnd.getMinutes() + duration);

            // Format times for display (HH:MM:SS format expected by service-details)
            const startTime = `${String(hours).padStart(2, '0')}:${String(minutes || 0).padStart(2, '0')}:00`;
            const endHours = eventEnd.getHours();
            const endMinutes = eventEnd.getMinutes();
            const endTime = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00`;

            allEvents.push({
              id: `worship-${worship.id}-${eventStart.toISOString()}`,
              title: `${worship.name} (Recurring)`,
              borderColor: "transparent",
              start: eventStart,
              end: eventEnd,
              className: "info service-type-worship worship-recurring",
              extendedProps: {
                origin: {
                  id: `worship-${worship.id}-${eventStart.toISOString()}`,
                  name: worship.name,
                  date: eventStart,
                  startAt: startTime,
                  endAt: endTime,
                  type: 'Worship',
                  schedule: 'weekly',
                  location: worship.location,
                  campus: worship.campus,
                  description: worship.description,
                  duration: worship.duration,
                  users: [],
                  isWorshipService: true,
                  worshipServiceId: worship.id,
                },
                serviceType: 'worship',
                isRecurring: true,
              },
            });
          }
          current.setDate(current.getDate() + 1);
        }
      });
    }

    return allEvents;
  }, [filteredServices, worshipServices, activeDate]);

  const onDelete = (id) => {
    const index = churchServices?.findIndex((m) => m.id === id);

    if (index !== -1) {
      setChurchServices(
        churchServices?.slice(0, index).concat(churchServices?.slice(index + 1))
      );
    }
    onClose();
  };

  useEffect(() => {
    setReadonlyForm(
      matchPath(
        "/admin/service-scheduling/services/:id/edit",
        location.pathname
      ) === null
    );
  }, [location]);

  useEffect(() => {
    if (!service?.id && serviceId && serviceId !== "new") {
      AtriumApi.get(`/v1/services/${serviceId}`).then((resp) => {
        setSelected(resp?.data?.service?.users);
        setService(resp?.data?.service);
      });
    }
  }, [service, serviceId]);

  const queryBuilder = useCallback(() => {
    const query = new URLSearchParams();

    // Always include the current date (M/D/YYYY) for calendar week filtering
    if (activeDate) {
      query.append("date", dayjs(activeDate).format("M/D/YYYY"));
    }

    if (searchTerm.trim()) {
      query.append("name", searchTerm.trim());
    }

    return `/v1/services?${query.toString()}`;
  }, [searchTerm, activeDate]);

  const getServices = useCallback(async (forceRefreshWorship = false) => {
    try {
      setLoading(true);
      setLoadError(null);

      // Only fetch worship services if we haven't loaded them yet OR if force refresh
      // They don't change frequently, so cache them for the session
      const shouldFetchWorship = worshipServices.length === 0 || forceRefreshWorship;

      const promises = [AtriumApi.get(queryBuilder())];

      if (shouldFetchWorship) {
        promises.push(
          AtriumApi.get("/v1/attendance/services").catch(() => ({ data: [] }))
        );
      }

      const responses = await Promise.all(promises);
      const servicesResponse = responses[0];
      const worshipResponse = shouldFetchWorship ? responses[1] : null;

      if (servicesResponse) {
        const services = servicesResponse?.data?.services ?? [];
        setChurchServices(services);
      }

      if (worshipResponse?.data) {
        setWorshipServices(Array.isArray(worshipResponse.data) ? worshipResponse.data : []);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [queryBuilder, worshipServices.length]);

  // Refresh all calendar data (services + worship services)
  const refreshCalendar = useCallback(() => {
    getServices(true); // Force refresh worship services too
  }, [getServices]);

  useEffect(() => {
    if (activeDate) {
      getServices();
    }
  }, [activeDate, searchTerm, getServices]);

  const newService = () => {
    setService({});
    setSelected([]);
    setReadonlyForm(false);
    onOpen();
    navigate("/admin/service-scheduling/services/new");
  };

  // Click empty slot to create service with pre-filled date/time
  const handleDateClick = (info) => {
    const clickedDate = info.date;

    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clickedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Format start time as HH:mm:ss
    const hours = String(clickedDate.getHours()).padStart(2, '0');
    const minutes = String(clickedDate.getMinutes()).padStart(2, '0');
    const startTime = `${hours}:${minutes}:00`;

    // End time is 1 hour later
    const endDate = new Date(clickedDate.getTime() + 60 * 60 * 1000);
    const endHours = String(endDate.getHours()).padStart(2, '0');
    const endMinutes = String(endDate.getMinutes()).padStart(2, '0');
    const endTime = `${endHours}:${endMinutes}:00`;

    // Pre-fill service with date and time
    setService({
      date: dateStr,
      startAt: startTime,
      endAt: endTime,
      schedule: "once",
    });
    setSelected([]);
    setReadonlyForm(false);
    onOpen();
    navigate("/admin/service-scheduling/services/new");
  };

  // Hover preview handlers
  const handleEventMouseEnter = (info) => {
    // Disable hover preview on touch devices (mobile/tablet)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      return;
    }

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Show preview after brief delay (Fantastical-style)
    hoverTimeoutRef.current = setTimeout(() => {
      const rect = info.el.getBoundingClientRect();

      // Smart positioning: position to the right, but check if it would go off screen
      let x = rect.right + 10;
      let y = rect.top;

      // If preview would go off right edge, position to the left instead
      const previewWidth = 320; // w-80 = 320px
      if (x + previewWidth > window.innerWidth) {
        x = rect.left - previewWidth - 10;
      }

      // If preview would go off bottom, adjust y
      const previewHeight = 400; // approximate
      if (y + previewHeight > window.innerHeight) {
        y = Math.max(10, window.innerHeight - previewHeight - 10);
      }

      setHoverPosition({ x, y });
      setHoveredEvent(info.event);
    }, 500); // 500ms delay
  };

  const handleEventMouseLeave = () => {
    // Clear timeout if user moves away before preview shows
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Add delay before hiding to allow mouse to move to preview
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredEvent(null);
    }, 300); // 300ms grace period to move mouse to preview
  };

  // Preview hover handlers
  const handlePreviewMouseEnter = () => {
    // Cancel any pending hide timeout when mouse enters preview
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePreviewMouseLeave = () => {
    // Hide preview when mouse leaves it
    setHoveredEvent(null);
  };

  // Hover preview actions
  const handlePreviewEdit = () => {
    if (!hoveredEvent) return;

    const serviceData = hoveredEvent.extendedProps.origin;
    setService(serviceData);
    setSelected(serviceData.users || []);
    setReadonlyForm(true);
    setHoveredEvent(null);
    onOpen();
    navigate("/admin/service-scheduling/services/" + hoveredEvent.id);
  };

  const handlePreviewDuplicate = () => {
    if (!hoveredEvent) return;

    const serviceData = hoveredEvent.extendedProps.origin;
    // Copy service but remove id to create new one
    const { id, ...serviceCopy } = serviceData;
    setService(serviceCopy);
    setSelected(serviceCopy.users || []);
    setReadonlyForm(false);
    setHoveredEvent(null);
    onOpen();
    navigate("/admin/service-scheduling/services/new");
  };

  const handlePreviewDelete = async () => {
    if (!hoveredEvent) return;

    const serviceId = hoveredEvent.id;
    const serviceName = hoveredEvent.extendedProps?.origin?.name || hoveredEvent.title;
    setHoveredEvent(null);

    const confirmed = await confirm({
      title: `Delete "${serviceName}"?`,
      message: "This service will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await AtriumApi.delete(`/v1/services/${serviceId}`);
      notify.success("Service deleted");
      getServices();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete service"));
    }
  };

  // Drag & drop handler
  const handleEventDrop = async (info) => {
    const { event } = info;
    const serviceId = event.id;
    const newStart = event.start;
    const newEnd = event.end;

    // Format date and times for API
    const dateStr = newStart.toISOString().split('T')[0];
    const startTime = `${String(newStart.getHours()).padStart(2, '0')}:${String(newStart.getMinutes()).padStart(2, '0')}:00`;
    const endTime = `${String(newEnd.getHours()).padStart(2, '0')}:${String(newEnd.getMinutes()).padStart(2, '0')}:00`;

    try {
      // Optimistically update the UI (FullCalendar already moved it)
      // If API fails, we'll revert

      await AtriumApi.put(`/v1/services/${serviceId}`, {
        date: dateStr,
        startAt: startTime,
        endAt: endTime,
      });

      notify.success("Service rescheduled");
      getServices(); // Refresh to ensure consistency
    } catch (error) {
      // Revert the event on failure
      info.revert();
      notify.error(getApiErrorMessage(error, "Could not reschedule service"));
    }
  };

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Keyboard shortcuts - Fantastical style
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ignore modified keys, keys already handled (e.g. arrows in the view
      // switcher) and typing in form fields or dialogs.
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (
        event.target.closest?.(
          "input, textarea, select, [contenteditable='true'], [role='dialog']"
        )
      ) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 't':
          gotoToday();
          break;
        case 'd':
          handleViewChange('timeGridDay');
          break;
        case 'w':
          handleViewChange('timeGridWeek');
          break;
        case 'm':
          handleViewChange('dayGridMonth');
          break;
        case 'arrowleft':
          prevWeek();
          break;
        case 'arrowright':
          nextWeek();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentView]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const overHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.add("ring-2", "ring-primary", "pointer-events-none");
        event.dataTransfer.dropEffect = "link";
        event.preventDefault();
      }
    };

    const leaveHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card === event.target) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.remove("ring-2", "ring-primary", "pointer-events-none");
      }

      if (card) {
        event.preventDefault();
      }
    };

    const dropHandler = (event) => {
      const card = event.target?.closest("div.fc-event-main");
      if (card === event.target) {
        const cardInner = card.querySelector("div.event-card");
        cardInner.classList.remove("ring-2", "ring-primary", "pointer-events-none");
        const id = parseInt(cardInner.getAttribute("data-id"));
        const userId = parseInt(event.dataTransfer.getData("text"));
        if (isNaN(userId)) return;
        const index = churchServices?.findIndex((model) => model.id === id);
        if (index === -1) return;

        const service = churchServices[index];
        if (service?.users?.findIndex((user) => user?.id === userId) !== -1)
          return;

        cardInner.classList.add("animate-pulse");
        setLoading(true);
        AtriumApi.put(`/v1/services/${id}/people`, {
          users: (service?.users || [])?.map((u) => u?.id)?.concat([userId]),
        })
          .then(() => {
            notify.success("Person assigned");
            getServices();
          })
          .catch((error) => {
            notify.error(getApiErrorMessage(error, "Could not assign person"));
          })
          .finally(() => {
            cardInner.classList.remove("animate-pulse");
            setLoading(false);
          });
      }
    };

    el.addEventListener("dragenter", overHandler);
    el.addEventListener("dragover", overHandler);
    el.addEventListener("dragleave", leaveHandler);
    el.addEventListener("drop", dropHandler);
    return () => {
      el.removeEventListener("dragenter", overHandler);
      el.removeEventListener("dragover", overHandler);
      el.removeEventListener("dragleave", leaveHandler);
      el.removeEventListener("drop", dropHandler);
    };
  }, [ref, churchServices, getServices]);

  const prevWeek = () => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.prev();

    const newDate = calendarApi.getDate();
    setActiveDate(newDate);
    setTitle(calendarApi.view.title);
  };

  const nextWeek = () => {
    if (!calendarRef.current) return;

    const calendarApi = calendarRef.current.getApi();
    calendarApi.next();

    const newDate = calendarApi.getDate();
    setActiveDate(newDate);
    setTitle(calendarApi.view.title);
  };

  const gotoDate = (date) => {
    if (!calendarRef.current) return;
    calendarRef.current.getApi().gotoDate(date);
    setActiveDate(calendarRef.current?.getApi().getDate());
    setTitle(calendarRef.current?.getApi().view.title);
  };

  const gotoToday = () => {
    if (!calendarRef.current) return;
    const today = new Date();
    calendarRef.current.getApi().gotoDate(today);
    setActiveDate(today);
    setTitle(calendarRef.current?.getApi().view.title);
  };

  const handleViewChange = (newView) => {
    if (!calendarRef.current) return;
    setCurrentView(newView);
    calendarRef.current.getApi().changeView(newView);
    setTitle(calendarRef.current?.getApi().view.title);
  };

  useEffect(() => {
    if (!calendarRef.current) return;
    setActiveDate(calendarRef.current?.getApi().getDate());
    setTitle(calendarRef.current?.getApi().view.title);
  }, [calendarRef]);

  const drawerProps = { isOpen, onClose };

  return (
    <PageContainer variant="bleed" className="gap-5">
      <PageHeader
        title="Scheduling"
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<MdRefresh aria-hidden="true" />}
              onClick={refreshCalendar}
            >
              Refresh
            </Button>
            <Button
              variant="secondary"
              leftIcon={<MdPersonAdd aria-hidden="true" />}
              onClick={() => {
                setShowAssign(true);
                setSelected([]);
              }}
            >
              Assign People
            </Button>
            <Button leftIcon={<MdAdd aria-hidden="true" />} onClick={newService}>
              Create Service
            </Button>
          </>
        }
      />

      <Card className="min-h-0 flex-1">
        <div className="flex flex-col gap-3 border-b border-line p-4">
          <CalendarHeader
            title={title}
            activeDate={activeDate}
            currentView={currentView}
            onViewChange={handleViewChange}
            onPrev={prevWeek}
            onNext={nextWeek}
            onToday={gotoToday}
            onDateSelect={gotoDate}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
          <QuickFilters onFilterChange={setFilters} churchServices={churchServices} />
        </div>

        <div className="flex min-h-0 flex-1 flex-row p-4">
          {loadError ? (
            <ErrorState
              className="flex-1"
              message={getApiErrorMessage(loadError, "Could not load services.")}
              onRetry={() => getServices()}
            />
          ) : (
            <div
              className={`relative w-full flex-1 ${
                showAssign ? "h-[calc(100dvh_-_270px)]" : "h-full"
              }`}
            >
              <div
                ref={ref}
                className={`w-full ${
                  showAssign ? "absolute left-0 top-0 h-full overflow-auto" : ""
                }`}
              >
                <EventCalendar
                  ref={calendarRef}
                  initialDate={activeDate}
                  currentView={currentView}
                  calendarData={events}
                  onViewMount={(info) => {
                    // Update title when calendar view mounts
                    setTitle(info.view.title);
                  }}
                  dateClick={handleDateClick}
                  eventClick={({ event }) => {
                    const serviceData = event.extendedProps.origin;

                    setService(serviceData);
                    setSelected(serviceData.users || []);
                    setReadonlyForm(true);
                    onOpen();

                    // Recurring worship services only open the drawer
                    if (!serviceData.isWorshipService) {
                      navigate("/admin/service-scheduling/services/" + event.id);
                    }
                  }}
                  eventMouseEnter={handleEventMouseEnter}
                  eventMouseLeave={handleEventMouseLeave}
                  editable={true}
                  eventDrop={handleEventDrop}
                />
              </div>
              {loading && <Loader variant="overlay" label="Loading services" />}
            </div>
          )}

          {showAssign && (
            <aside
              aria-labelledby="assign-people-heading"
              className="ml-4 flex h-[calc(100dvh_-_270px)] w-80 flex-none select-none flex-col gap-3 overflow-auto border-l border-line pl-4"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 id="assign-people-heading" className="text-heading">
                    Assign People
                  </h2>
                  <p className="mt-1 text-sm text-fg-muted">
                    Drag a person onto a service to assign them.
                  </p>
                </div>
                <IconButton
                  icon={<MdClose />}
                  label="Close Assign People"
                  size="sm"
                  onClick={() => setShowAssign(false)}
                />
              </div>
              <AssignForm
                draggable={true}
                selected={selected}
                setSelected={setSelected}
              />
            </aside>
          )}
        </div>
      </Card>

      {serviceId === "new" ? (
        <ServiceForm
          {...drawerProps}
          selected={selected}
          setSelected={setSelected}
          service={service}
          setService={setService}
          getServices={getServices}
        />
      ) : readonlyForm ? (
        <ServiceDetails
          {...drawerProps}
          service={service}
          onDelete={onDelete}
          getServices={getServices}
          type="editTemplate"
        />
      ) : (
        <ServiceForm
          {...drawerProps}
          selected={selected}
          setSelected={setSelected}
          service={service}
          setService={setService}
          getServices={getServices}
        />
      )}

      {hoveredEvent && (
        <EventHoverPreview
          event={hoveredEvent}
          position={hoverPosition}
          onEdit={handlePreviewEdit}
          onDuplicate={handlePreviewDuplicate}
          onDelete={handlePreviewDelete}
          onMouseEnter={handlePreviewMouseEnter}
          onMouseLeave={handlePreviewMouseLeave}
        />
      )}
    </PageContainer>
  );
};

export default Calendar;
